/* eslint-disable */
// -----------------------------------------------------------------------------
// Indicators page — FRONTEND MOCK ONLY.
// No API yet. The goal here is to validate UX/layout before we design the
// generic backend contract. Data below is illustrative for CCU Argentina.
// -----------------------------------------------------------------------------
import React, { useMemo, useState } from "react";
import cx from "classnames";
import { useTranslation } from "react-i18next";
import useThemeColors from "../../hooks/useThemeColors";

// @material-ui/core
import { makeStyles } from "@material-ui/core/styles";
import Tooltip from "@material-ui/core/Tooltip";

// @material-ui/icons
import LocalShipping from "@material-ui/icons/LocalShipping";
import Storefront from "@material-ui/icons/Storefront";
import PrecisionManufacturing from "@material-ui/icons/Build";
import TrendingUp from "@material-ui/icons/TrendingUp";
import TrendingDown from "@material-ui/icons/TrendingDown";
import TrendingFlat from "@material-ui/icons/TrendingFlat";
import InfoOutlined from "@material-ui/icons/InfoOutlined";
import ArrowUpward from "@material-ui/icons/ArrowUpward";
import ArrowDownward from "@material-ui/icons/ArrowDownward";
import UnfoldMore from "@material-ui/icons/UnfoldMore";
import FilterList from "@material-ui/icons/FilterList";
import Close from "@material-ui/icons/Close";

// -----------------------------------------------------------------------------
// Mock metadata (shape we are converging to — see docs/FEATURE_INDICATORS.md)
// -----------------------------------------------------------------------------
const MOCK_CATEGORIES = [
  {
    code: "logistics",
    name: { en: "Logistics", es: "Logística", pt: "Logística" },
    icon: LocalShipping,
    boards: [
      "logistics_efficiency",
      "cd_comparison",
      "transport_mix",
      "route_costs",
    ],
  },
  {
    code: "commercial",
    name: { en: "Commercial", es: "Comercial", pt: "Comercial" },
    icon: Storefront,
    boards: ["channel_performance", "client_concentration"],
  },
  {
    code: "manufacturing",
    name: { en: "Manufacturing", es: "Fabricación", pt: "Fabricação" },
    icon: PrecisionManufacturing,
    boards: ["plant_efficiency"],
  },
];

const MOCK_BOARDS = {
  // ---------------------------------------------------------------------------
  logistics_efficiency: {
    code: "logistics_efficiency",
    name: {
      en: "Logistics Efficiency",
      es: "Eficiencia Logística",
      pt: "Eficiência Logística",
    },
    template: "scorecard_ranking",
    description: {
      es: "Costo logístico por Centro de Distribución. Identifica CDs con costos superiores al promedio.",
      pt: "Custo logístico por Centro de Distribuição. Identifica CDs com custos acima da média.",
      en: "Logistics cost per Distribution Center. Identifies CDs with costs above the average.",
    },
    kpis: [
      {
        code: "cost_per_pallet",
        label: { es: "Costo / Pallet", pt: "Custo / Pallet", en: "Cost / Pallet" },
        value: 8420,
        delta: +3.2,
        format: "currency_ars",
        trend: "lower_is_better",
      },
      {
        code: "total_volume_hl",
        label: { es: "Volumen (HL)", pt: "Volume (HL)", en: "Volume (HL)" },
        value: 482350,
        delta: +1.1,
        format: "number",
        trend: "higher_is_better",
      },
      {
        code: "pct_porteo",
        label: { es: "% Porteo", pt: "% Porteio", en: "% Porteo" },
        value: 0.284,
        delta: -0.8,
        format: "percent",
        trend: "neutral",
      },
      {
        code: "avg_occupancy",
        label: { es: "Ocupación Flota", pt: "Ocupação Frota", en: "Fleet Occupancy" },
        value: 0.726,
        delta: +2.4,
        format: "percent",
        trend: "higher_is_better",
      },
    ],
    ranking: {
      dimension: "deposito_origen",
      dimensionLabel: { es: "Centro de Distribución", pt: "Centro de Distribuição", en: "Distribution Center" },
      metric: "cost_per_pallet",
      metricLabel: { es: "Costo / Pallet", pt: "Custo / Pallet", en: "Cost / Pallet" },
      benchmark: 8420,
      format: "currency_ars",
      trend: "lower_is_better",
      items: [
        { name: "CD Mendoza", value: 12840, volume: 24560 },
        { name: "CD Bahía Blanca", value: 11920, volume: 18730 },
        { name: "CD Córdoba", value: 10450, volume: 62340 },
        { name: "CD Mar del Plata", value: 9810, volume: 32110 },
        { name: "CD Rosario", value: 8940, volume: 54220 },
        { name: "CD Tucumán", value: 8620, volume: 21480 },
        { name: "CD San Juan", value: 8290, volume: 16940 },
        { name: "CD Luján", value: 7860, volume: 78640 },
        { name: "CD La Plata", value: 7520, volume: 41230 },
        { name: "CD Salta", value: 7240, volume: 14820 },
        { name: "CD Resistencia", value: 6980, volume: 12450 },
        { name: "CD Neuquén", value: 6710, volume: 9820 },
      ],
    },
  },
  // ---------------------------------------------------------------------------
  cd_comparison: {
    code: "cd_comparison",
    name: {
      en: "CD Comparison",
      es: "Comparación de CDs",
      pt: "Comparação de CDs",
    },
    template: "heatmap_matrix",
    description: {
      es: "Matriz de indicadores por CD. Colores indican desempeño vs. el promedio de la red.",
      pt: "Matriz de indicadores por CD. Cores indicam performance vs. média da rede.",
      en: "Indicator matrix per CD. Colors indicate performance vs. network average.",
    },
    rows_dimension: {
      code: "deposito_origen",
      label: { es: "Centro de Distribución", pt: "Centro de Distribuição", en: "Distribution Center" },
    },
    metrics: [
      {
        code: "volume_pallets",
        label: { es: "Volumen (Pallets)", pt: "Volume (Pallets)", en: "Volume (Pallets)" },
        format: "number",
        color_scale: "neutral",
      },
      {
        code: "cost_per_pallet",
        label: { es: "Costo / Pallet", pt: "Custo / Pallet", en: "Cost / Pallet" },
        format: "currency_ars",
        color_scale: "lower_is_better",
      },
      {
        code: "pct_porteo",
        label: { es: "% Porteo", pt: "% Porteio", en: "% Porteo" },
        format: "percent",
        color_scale: "lower_is_better",
      },
      {
        code: "avg_occupancy",
        label: { es: "Ocup. Flota", pt: "Ocup. Frota", en: "Fleet Occ." },
        format: "percent",
        color_scale: "higher_is_better",
      },
      {
        code: "avg_distance_km",
        label: { es: "Dist. Media (km)", pt: "Dist. Média (km)", en: "Avg Distance (km)" },
        format: "number",
        color_scale: "lower_is_better",
      },
    ],
    rows: [
      { name: "CD Córdoba", values: [62340, 10450, 0.32, 0.78, 142] },
      { name: "CD Luján", values: [78640, 7860, 0.21, 0.82, 78] },
      { name: "CD Rosario", values: [54220, 8940, 0.29, 0.71, 118] },
      { name: "CD La Plata", values: [41230, 7520, 0.18, 0.85, 62] },
      { name: "CD Mar del Plata", values: [32110, 9810, 0.35, 0.64, 186] },
      { name: "CD Mendoza", values: [24560, 12840, 0.42, 0.58, 264] },
      { name: "CD Tucumán", values: [21480, 8620, 0.28, 0.73, 158] },
      { name: "CD Bahía Blanca", values: [18730, 11920, 0.38, 0.61, 212] },
      { name: "CD San Juan", values: [16940, 8290, 0.31, 0.69, 174] },
      { name: "CD Salta", values: [14820, 7240, 0.24, 0.76, 148] },
      { name: "CD Resistencia", values: [12450, 6980, 0.22, 0.79, 132] },
      { name: "CD Neuquén", values: [9820, 6710, 0.19, 0.81, 98] },
    ],
  },
  // ---------------------------------------------------------------------------
  transport_mix: {
    code: "transport_mix",
    name: {
      en: "Transport Mix",
      es: "Mix de Transporte",
      pt: "Mix de Transporte",
    },
    template: "decomposition",
    description: {
      es: "Distribución del costo logístico por tipo de transporte y canal.",
      pt: "Distribuição do custo logístico por tipo de transporte e canal.",
      en: "Logistics cost breakdown by transport type and channel.",
    },
    total_label: { es: "Costo Logístico Total", pt: "Custo Logístico Total", en: "Total Logistics Cost" },
    total_value: 4060_000_000,
    format: "currency_ars",
    groups: [
      {
        name: { es: "T1 (Directo)", pt: "T1 (Direto)", en: "T1 (Direct)" },
        value: 1_810_000_000,
        color: "#3b82f6",
      },
      {
        name: { es: "Porteo", pt: "Porteio", en: "Porteo" },
        value: 1_150_000_000,
        color: "#8b5cf6",
      },
      {
        name: { es: "Interdepósitos", pt: "Interdepósitos", en: "Interdepots" },
        value: 620_000_000,
        color: "#06b6d4",
      },
      {
        name: { es: "Redistribución SMK", pt: "Redistribuição SMK", en: "SMK Redistribution" },
        value: 310_000_000,
        color: "#f59e0b",
      },
      {
        name: { es: "Aforo", pt: "Aforo", en: "Aforo" },
        value: 170_000_000,
        color: "#10b981",
      },
    ],
  },
  // ---------------------------------------------------------------------------
  channel_performance: {
    code: "channel_performance",
    name: {
      en: "Channel Performance",
      es: "Performance de Canales",
      pt: "Performance de Canais",
    },
    template: "scorecard_ranking",
    description: {
      es: "Margen de servir por canal. Detecta canales con baja rentabilidad.",
      pt: "Margem de servir por canal. Detecta canais com baixa rentabilidade.",
      en: "Serve margin by channel. Detects low-profitability channels.",
    },
    kpis: [
      {
        code: "total_revenue",
        label: { es: "Venta Bruta", pt: "Venda Bruta", en: "Gross Revenue" },
        value: 42_300_000_000,
        delta: +4.6,
        format: "currency_ars_compact",
        trend: "higher_is_better",
      },
      {
        code: "gross_margin",
        label: { es: "Margen Bruto", pt: "Margem Bruta", en: "Gross Margin" },
        value: 0.382,
        delta: -0.4,
        format: "percent",
        trend: "higher_is_better",
      },
      {
        code: "serve_margin",
        label: { es: "Margen de Servir", pt: "Margem de Servir", en: "Serve Margin" },
        value: 0.187,
        delta: +0.8,
        format: "percent",
        trend: "higher_is_better",
      },
      {
        code: "active_clients",
        label: { es: "Clientes Activos", pt: "Clientes Ativos", en: "Active Clients" },
        value: 24830,
        delta: +1.2,
        format: "number",
        trend: "higher_is_better",
      },
    ],
    ranking: {
      dimension: "canal_1",
      dimensionLabel: { es: "Canal", pt: "Canal", en: "Channel" },
      metric: "serve_margin",
      metricLabel: { es: "Margen de Servir", pt: "Margem de Servir", en: "Serve Margin" },
      benchmark: 0.187,
      format: "percent",
      trend: "higher_is_better",
      items: [
        { name: "Supermercados", value: 0.242, volume: 124800 },
        { name: "Mayoristas", value: 0.218, volume: 96400 },
        { name: "Almacenes", value: 0.196, volume: 72300 },
        { name: "Gastronomía", value: 0.174, volume: 54200 },
        { name: "Kioscos", value: 0.152, volume: 38600 },
        { name: "Estaciones de Servicio", value: 0.128, volume: 24800 },
        { name: "Hoteles / Resorts", value: 0.086, volume: 14200 },
      ],
    },
  },
  // ---------------------------------------------------------------------------
  client_concentration: {
    code: "client_concentration",
    name: {
      en: "Client Concentration",
      es: "Concentración de Clientes",
      pt: "Concentração de Clientes",
    },
    template: "decomposition",
    description: {
      es: "Top 10 clientes representan el 48% del volumen. Alta dependencia comercial.",
      pt: "Top 10 clientes representam 48% do volume. Alta dependência comercial.",
      en: "Top 10 clients represent 48% of volume. High commercial dependency.",
    },
    total_label: { es: "Volumen Total (HL)", pt: "Volume Total (HL)", en: "Total Volume (HL)" },
    total_value: 482_350,
    format: "number",
    groups: [
      { name: { es: "Top 10 Clientes", pt: "Top 10 Clientes", en: "Top 10 Clients" }, value: 231_528, color: "#ef4444" },
      { name: { es: "Top 11–50", pt: "Top 11–50", en: "Top 11–50" }, value: 144_705, color: "#f59e0b" },
      { name: { es: "Top 51–500", pt: "Top 51–500", en: "Top 51–500" }, value: 77_176, color: "#10b981" },
      { name: { es: "Resto", pt: "Restante", en: "Rest" }, value: 28_941, color: "#6b7280" },
    ],
  },
  // ---------------------------------------------------------------------------
  // REAL CASE — CCU Argentina — Route costs (real data design, mock data here)
  // ---------------------------------------------------------------------------
  route_costs: {
    code: "route_costs",
    name: {
      en: "Route Costs",
      es: "Costos por Ruta",
      pt: "Custos por Rota",
    },
    template: "route_table",
    description: {
      es: "Rastreá el flujo de un producto con visión financiera. Identificá rutas ineficientes por costo por pallet y por km.",
      pt: "Rastreie o fluxo de um produto com visão financeira. Identifique rotas ineficientes por custo por pallet e por km.",
      en: "Track product flow with financial view. Identify inefficient routes by cost per pallet and per km.",
    },
    // Filters declared in metadata — the frontend just renders them.
    filters: [
      { code: "sku",       label: { es: "SKU", pt: "SKU", en: "SKU" } },
      { code: "cliente",   label: { es: "Cliente", pt: "Cliente", en: "Client" } },
      { code: "cd",        label: { es: "CD", pt: "CD", en: "DC" } },
      { code: "origen",    label: { es: "Origen", pt: "Origem", en: "Origin" } },
      { code: "destino",   label: { es: "Destino", pt: "Destino", en: "Destination" } },
      { code: "periodo",   label: { es: "Período", pt: "Período", en: "Period" } },
    ],
    // Column groups (visual separation in header).
    columnGroups: [
      { label: { es: "Flujo", pt: "Fluxo", en: "Flow" }, cols: ["origen", "destino"] },
      { label: { es: "Selección", pt: "Seleção", en: "Selection" }, cols: ["cajas", "pallets", "km"] },
      { label: { es: "Ruta", pt: "Rota", en: "Route" }, cols: ["pallets_total", "cost_total", "cost_per_pallet", "cost_per_pallet_km"] },
    ],
    columns: [
      { code: "origen",             label: { es: "Origen", pt: "Origem", en: "Origin" },               type: "text" },
      { code: "destino",            label: { es: "Destino", pt: "Destino", en: "Destination" },        type: "text" },
      { code: "cajas",              label: { es: "Cajas", pt: "Caixas", en: "Boxes" },                 type: "number",       format: "number" },
      { code: "pallets",            label: { es: "Pallets", pt: "Pallets", en: "Pallets" },            type: "number",       format: "number" },
      { code: "km",                 label: { es: "km", pt: "km", en: "km" },                           type: "number",       format: "number" },
      { code: "pallets_total",      label: { es: "Pallets total", pt: "Pallets total", en: "Pallets total" }, type: "number", format: "number" },
      { code: "cost_total",         label: { es: "$ total", pt: "$ total", en: "$ total" },            type: "number",       format: "currency_ars_compact" },
      { code: "cost_per_pallet",    label: { es: "$/pallet", pt: "$/pallet", en: "$/pallet" },         type: "number",       format: "currency_ars", color_scale: "lower_is_better" },
      { code: "cost_per_pallet_km", label: { es: "$/pallet/km", pt: "$/pallet/km", en: "$/pallet/km" },type: "number",       format: "currency_ars", color_scale: "lower_is_better", decimals: 2 },
    ],
    defaultSort: { column: "cost_per_pallet", order: "desc" },
    rows: [
      // deposit → deposit (inter-depósito / Acarreo)
      { origen: "CD Luján",        destino: "CD Córdoba",       cajas: null,    pallets: null,  km: 720,  pallets_total: 3840, cost_total: 284_160_000, cost_per_pallet: 74000, cost_per_pallet_km: 102.8, origen_type: "deposit", destino_type: "deposit" },
      { origen: "CD Luján",        destino: "CD Rosario",       cajas: null,    pallets: null,  km: 320,  pallets_total: 2140, cost_total: 68_480_000,  cost_per_pallet: 32000, cost_per_pallet_km: 100.0, origen_type: "deposit", destino_type: "deposit" },
      { origen: "CD Luján",        destino: "CD Mendoza",       cajas: null,    pallets: null,  km: 1060, pallets_total: 1820, cost_total: 194_740_000, cost_per_pallet: 107000, cost_per_pallet_km: 100.9, origen_type: "deposit", destino_type: "deposit" },
      { origen: "CD Córdoba",      destino: "CD Tucumán",       cajas: null,    pallets: null,  km: 540,  pallets_total: 1420, cost_total: 78_880_000,  cost_per_pallet: 55550, cost_per_pallet_km: 102.9, origen_type: "deposit", destino_type: "deposit" },
      { origen: "CD Mendoza",      destino: "CD San Juan",      cajas: null,    pallets: null,  km: 170,  pallets_total: 820,  cost_total: 14_350_000,  cost_per_pallet: 17500, cost_per_pallet_km: 102.9, origen_type: "deposit", destino_type: "deposit" },
      { origen: "CD Bahía Blanca", destino: "CD Mar del Plata", cajas: null,    pallets: null,  km: 480,  pallets_total: 640,  cost_total: 34_560_000,  cost_per_pallet: 54000, cost_per_pallet_km: 112.5, origen_type: "deposit", destino_type: "deposit" },
      { origen: "SAN JUAN",        destino: "CD Mendoza",       cajas: null,    pallets: null,  km: 170,  pallets_total: 480,  cost_total: 8_440_000,   cost_per_pallet: 17583, cost_per_pallet_km: 103.4, origen_type: "deposit", destino_type: "deposit" },
      // deposit → Clientes T1 (aggregated T1 deliveries from a depot)
      { origen: "CD Luján",        destino: "Clientes T1",      cajas: 182_400, pallets: 7296,  km: 88,   pallets_total: 7296, cost_total: 192_384_000, cost_per_pallet: 26368, cost_per_pallet_km: 299.6, origen_type: "deposit", destino_type: "client_t1" },
      { origen: "CD Córdoba",      destino: "Clientes T1",      cajas: 134_200, pallets: 5368,  km: 142,  pallets_total: 5368, cost_total: 286_624_000, cost_per_pallet: 53400, cost_per_pallet_km: 376.1, origen_type: "deposit", destino_type: "client_t1" },
      { origen: "CD Rosario",      destino: "Clientes T1",      cajas: 98_800,  pallets: 3952,  km: 118,  pallets_total: 3952, cost_total: 151_008_000, cost_per_pallet: 38210, cost_per_pallet_km: 323.8, origen_type: "deposit", destino_type: "client_t1" },
      { origen: "CD Mendoza",      destino: "Clientes T1",      cajas: 64_500,  pallets: 2580,  km: 264,  pallets_total: 2580, cost_total: 193_128_000, cost_per_pallet: 74856, cost_per_pallet_km: 283.5, origen_type: "deposit", destino_type: "client_t1" },
      { origen: "CD Tucumán",      destino: "Clientes T1",      cajas: 42_300,  pallets: 1692,  km: 158,  pallets_total: 1692, cost_total: 82_008_000,  cost_per_pallet: 48468, cost_per_pallet_km: 306.8, origen_type: "deposit", destino_type: "client_t1" },
      { origen: "CD Mar del Plata",destino: "Clientes T1",      cajas: 58_200,  pallets: 2328,  km: 186,  pallets_total: 2328, cost_total: 186_240_000, cost_per_pallet: 80000, cost_per_pallet_km: 430.1, origen_type: "deposit", destino_type: "client_t1" },
      { origen: "CD Bahía Blanca", destino: "Clientes T1",      cajas: 34_800,  pallets: 1392,  km: 212,  pallets_total: 1392, cost_total: 118_320_000, cost_per_pallet: 85000, cost_per_pallet_km: 400.9, origen_type: "deposit", destino_type: "client_t1" },
      // deposit → Clientes Porteo
      { origen: "CD Luján",        destino: "Clientes Porteo",  cajas: 96_400,  pallets: 3856,  km: 42,   pallets_total: 3856, cost_total: 77_120_000,  cost_per_pallet: 20000, cost_per_pallet_km: 476.2, origen_type: "deposit", destino_type: "client_porteo" },
      { origen: "CD Córdoba",      destino: "Clientes Porteo",  cajas: 72_200,  pallets: 2888,  km: 68,   pallets_total: 2888, cost_total: 95_304_000,  cost_per_pallet: 33000, cost_per_pallet_km: 485.3, origen_type: "deposit", destino_type: "client_porteo" },
      { origen: "CD Rosario",      destino: "Clientes Porteo",  cajas: 54_100,  pallets: 2164,  km: 56,   pallets_total: 2164, cost_total: 60_592_000,  cost_per_pallet: 28000, cost_per_pallet_km: 500.0, origen_type: "deposit", destino_type: "client_porteo" },
      { origen: "CD Mendoza",      destino: "Clientes Porteo",  cajas: 28_400,  pallets: 1136,  km: 94,   pallets_total: 1136, cost_total: 58_872_000,  cost_per_pallet: 51820, cost_per_pallet_km: 551.3, origen_type: "deposit", destino_type: "client_porteo" },
      { origen: "CD Tucumán",      destino: "Clientes Porteo",  cajas: 16_800,  pallets: 672,   km: 78,   pallets_total: 672,  cost_total: 25_478_000,  cost_per_pallet: 37914, cost_per_pallet_km: 486.1, origen_type: "deposit", destino_type: "client_porteo" },
      { origen: "CD Salta",        destino: "Clientes Porteo",  cajas: 12_400,  pallets: 496,   km: 108,  pallets_total: 496,  cost_total: 24_304_000,  cost_per_pallet: 49000, cost_per_pallet_km: 453.7, origen_type: "deposit", destino_type: "client_porteo" },
    ],
  },
  // ---------------------------------------------------------------------------
  plant_efficiency: {
    code: "plant_efficiency",
    name: {
      en: "Plant Efficiency",
      es: "Eficiencia de Plantas",
      pt: "Eficiência de Plantas",
    },
    template: "benchmark_target",
    description: {
      es: "Ocupación de plantas vs. capacidad instalada.",
      pt: "Ocupação de plantas vs. capacidade instalada.",
      en: "Plant utilization vs. installed capacity.",
    },
    target: 0.85,
    format: "percent",
    items: [
      { name: "Planta Luján", actual: 0.92, target: 0.85 },
      { name: "Planta Salta", actual: 0.88, target: 0.85 },
      { name: "Planta Mendoza", actual: 0.82, target: 0.85 },
      { name: "Planta San Juan", actual: 0.74, target: 0.85 },
      { name: "Planta Tucumán", actual: 0.61, target: 0.85 },
    ],
  },
};

// -----------------------------------------------------------------------------
// Formatting helpers
// -----------------------------------------------------------------------------
function formatValue(value, format) {
  if (value == null || Number.isNaN(value)) return "–";
  switch (format) {
    case "currency_ars":
      return `$ ${value.toLocaleString("es-AR", { maximumFractionDigits: 0 })}`;
    case "currency_ars_compact": {
      const abs = Math.abs(value);
      if (abs >= 1e9) return `$ ${(value / 1e9).toFixed(1)} B`;
      if (abs >= 1e6) return `$ ${(value / 1e6).toFixed(1)} M`;
      if (abs >= 1e3) return `$ ${(value / 1e3).toFixed(1)} K`;
      return `$ ${value.toFixed(0)}`;
    }
    case "percent":
      return `${(value * 100).toFixed(1)}%`;
    case "number":
      return value.toLocaleString("es-AR", { maximumFractionDigits: 0 });
    default:
      return String(value);
  }
}

function formatDelta(delta) {
  if (delta == null) return "";
  const sign = delta > 0 ? "+" : "";
  return `${sign}${delta.toFixed(1)}%`;
}

function getTrendColor(delta, trend) {
  if (delta == null || trend === "neutral") return "#6b7280";
  const improving =
    (trend === "higher_is_better" && delta > 0) ||
    (trend === "lower_is_better" && delta < 0);
  if (Math.abs(delta) < 0.2) return "#6b7280";
  return improving ? "#10b981" : "#ef4444";
}

function getTrendIcon(delta, trend) {
  if (delta == null || Math.abs(delta) < 0.2) return TrendingFlat;
  if (trend === "higher_is_better") return delta > 0 ? TrendingUp : TrendingDown;
  if (trend === "lower_is_better") return delta < 0 ? TrendingUp : TrendingDown;
  return delta > 0 ? TrendingUp : TrendingDown;
}

// Color scale: returns background color for a cell based on value + neighbors
function getHeatColor(value, allValues, scale) {
  if (scale === "neutral") return "transparent";
  const sorted = [...allValues].sort((a, b) => a - b);
  const min = sorted[0];
  const max = sorted[sorted.length - 1];
  if (max === min) return "transparent";
  const normalized = (value - min) / (max - min);
  // higher_is_better: high = green; lower_is_better: low = green
  const good =
    scale === "higher_is_better" ? normalized : 1 - normalized;
  // good ∈ [0,1]: red → yellow → green
  const r = good < 0.5 ? 239 : Math.round(239 - (good - 0.5) * 2 * 223);
  const g = good < 0.5 ? Math.round(68 + good * 2 * 174) : Math.round(242 - (good - 0.5) * 2 * 53);
  const b = good < 0.5 ? 68 : Math.round(68 + (good - 0.5) * 2 * 61);
  return `rgba(${r}, ${g}, ${b}, 0.25)`;
}

// -----------------------------------------------------------------------------
// Styles
// -----------------------------------------------------------------------------
const useStyles = makeStyles((theme) => ({
  pageWrapper: {
    position: "relative",
    marginTop: "-28px",
  },
  root: {
    display: "flex",
    height: "calc(100vh - 110px)",
    minHeight: 600,
    fontFamily: theme.typography.fontFamily,
    background: "#fafafa",
    borderRadius: 8,
    overflow: "hidden",
  },
  // Sidebar (categories)
  sidebar: {
    width: 220,
    background: "#fff",
    borderRight: "1px solid #e5e7eb",
    padding: "20px 0",
    display: "flex",
    flexDirection: "column",
    gap: 4,
    flexShrink: 0,
  },
  sidebarTitle: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: 0.6,
    color: "#9ca3af",
    textTransform: "uppercase",
    padding: "0 20px 8px",
  },
  categoryItem: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "12px 20px",
    cursor: "pointer",
    fontSize: 14,
    color: "#4b5563",
    transition: "all 0.15s",
    borderLeft: "3px solid transparent",
    "&:hover": {
      background: "#f9fafb",
    },
  },
  categoryItemActive: {
    color: ({ clientColor }) => clientColor || "#1f2937",
    fontWeight: 600,
    background: "#f9fafb",
    borderLeftColor: ({ clientColor }) => clientColor || "#1f2937",
  },
  // Main content
  content: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  tabs: {
    display: "flex",
    gap: 4,
    padding: "16px 24px 0",
    background: "#fff",
    borderBottom: "1px solid #e5e7eb",
  },
  tab: {
    padding: "10px 18px",
    fontSize: 13,
    color: "#6b7280",
    cursor: "pointer",
    borderBottom: "2px solid transparent",
    transition: "all 0.15s",
    fontWeight: 500,
    "&:hover": { color: "#1f2937" },
  },
  tabActive: {
    color: ({ clientColor }) => clientColor || "#1f2937",
    borderBottomColor: ({ clientColor }) => clientColor || "#1f2937",
    fontWeight: 600,
  },
  boardHeader: {
    padding: "16px 24px 8px",
    background: "#fff",
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  boardTitle: {
    fontSize: 18,
    fontWeight: 600,
    color: "#111827",
    margin: 0,
  },
  boardDescription: {
    fontSize: 12,
    color: "#6b7280",
    marginTop: 2,
  },
  canvas: {
    flex: 1,
    padding: "16px 24px 24px",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },
  // KPIs row
  kpiRow: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: 12,
  },
  kpiCard: {
    background: "#fff",
    borderRadius: 8,
    padding: "14px 18px",
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
    border: "1px solid #f3f4f6",
  },
  kpiLabel: {
    fontSize: 11,
    color: "#6b7280",
    fontWeight: 500,
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
  kpiValue: {
    fontSize: 26,
    fontWeight: 700,
    color: "#111827",
    marginTop: 4,
    lineHeight: 1.1,
  },
  kpiDelta: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontSize: 12,
    fontWeight: 600,
    marginTop: 4,
  },
  // Ranking
  rankingCard: {
    background: "#fff",
    borderRadius: 8,
    padding: "16px 20px",
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
    border: "1px solid #f3f4f6",
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  rankingHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    marginBottom: 10,
  },
  rankingTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: "#374151",
  },
  rankingBenchmark: {
    fontSize: 11,
    color: "#6b7280",
  },
  rankingList: {
    flex: 1,
    overflow: "auto",
    paddingRight: 4,
  },
  rankingRow: {
    display: "grid",
    gridTemplateColumns: "1fr 180px 90px",
    alignItems: "center",
    padding: "6px 0",
    borderBottom: "1px solid #f3f4f6",
    gap: 12,
    cursor: "pointer",
    "&:hover": { background: "#fafafa" },
  },
  rankingName: {
    fontSize: 13,
    color: "#1f2937",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  barContainer: {
    position: "relative",
    height: 20,
    background: "#f3f4f6",
    borderRadius: 4,
  },
  bar: {
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    borderRadius: 4,
  },
  benchmarkLine: {
    position: "absolute",
    top: -2,
    bottom: -2,
    width: 2,
    background: "#1f2937",
  },
  rankingValue: {
    fontSize: 13,
    fontWeight: 600,
    color: "#111827",
    textAlign: "right",
  },
  // Heatmap
  heatmapCard: {
    background: "#fff",
    borderRadius: 8,
    padding: "16px 20px",
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
    border: "1px solid #f3f4f6",
    flex: 1,
    overflow: "auto",
  },
  heatTable: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 13,
  },
  heatThRow: {
    borderBottom: "2px solid #e5e7eb",
  },
  heatTh: {
    padding: "8px 12px",
    fontSize: 11,
    color: "#6b7280",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: 0.4,
    textAlign: "right",
    whiteSpace: "nowrap",
  },
  heatThFirst: {
    textAlign: "left",
  },
  heatRow: {
    borderBottom: "1px solid #f3f4f6",
  },
  heatCell: {
    padding: "8px 12px",
    textAlign: "right",
    fontWeight: 400,
    color: "#1f2937",
    transition: "background 0.15s",
  },
  heatCellFirst: {
    textAlign: "left",
    fontWeight: 500,
    background: "transparent !important",
  },
  // Decomposition
  decompCard: {
    background: "#fff",
    borderRadius: 8,
    padding: "20px 24px",
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
    border: "1px solid #f3f4f6",
    flex: 1,
    display: "flex",
    flexDirection: "column",
  },
  decompTotal: {
    textAlign: "center",
    marginBottom: 20,
  },
  decompTotalLabel: {
    fontSize: 11,
    color: "#6b7280",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  decompTotalValue: {
    fontSize: 32,
    fontWeight: 700,
    color: "#111827",
    marginTop: 4,
  },
  decompStacked: {
    display: "flex",
    height: 42,
    borderRadius: 6,
    overflow: "hidden",
    marginBottom: 14,
    boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.04)",
  },
  decompSegment: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    fontWeight: 600,
    fontSize: 12,
  },
  decompLegend: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
    gap: 8,
  },
  decompLegendItem: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "8px 12px",
    background: "#f9fafb",
    borderRadius: 6,
    fontSize: 13,
  },
  decompLegendSwatch: {
    width: 14,
    height: 14,
    borderRadius: 3,
  },
  decompLegendName: {
    color: "#1f2937",
    fontWeight: 500,
    flex: 1,
  },
  decompLegendValue: {
    color: "#111827",
    fontWeight: 600,
    fontSize: 12,
  },
  // Benchmark / target
  benchmarkCard: {
    background: "#fff",
    borderRadius: 8,
    padding: "20px 24px",
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
    border: "1px solid #f3f4f6",
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: 14,
  },
  bulletRow: {
    display: "grid",
    gridTemplateColumns: "200px 1fr 100px",
    alignItems: "center",
    gap: 16,
  },
  bulletName: {
    fontSize: 14,
    fontWeight: 500,
    color: "#1f2937",
  },
  bulletTrack: {
    position: "relative",
    height: 18,
    background: "#f3f4f6",
    borderRadius: 9,
  },
  bulletFill: {
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    borderRadius: 9,
    transition: "width 0.3s",
  },
  bulletTarget: {
    position: "absolute",
    top: -3,
    bottom: -3,
    width: 3,
    background: "#1f2937",
    borderRadius: 1,
  },
  bulletValue: {
    fontSize: 14,
    fontWeight: 600,
    color: "#111827",
    textAlign: "right",
  },
  // Route table template
  routeContainer: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: 12,
    overflow: "hidden",
  },
  filterBar: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
    background: "#fff",
    borderRadius: 8,
    padding: "10px 14px",
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
    border: "1px solid #f3f4f6",
  },
  filterBarIcon: {
    fontSize: 18,
    color: "#9ca3af",
    marginRight: 2,
  },
  filterField: {
    display: "flex",
    flexDirection: "column",
    minWidth: 128,
  },
  filterLabel: {
    fontSize: 10,
    fontWeight: 500,
    color: "#6b7280",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginBottom: 2,
  },
  filterSelect: {
    fontSize: 13,
    fontWeight: 400,
    padding: "6px 8px",
    border: "1px solid #e5e7eb",
    borderRadius: 4,
    background: "#fff",
    color: "#1f2937",
    minWidth: 128,
    "&:hover": { borderColor: "#d1d5db" },
    "&:focus": {
      outline: "none",
      borderColor: ({ clientColor }) => clientColor || "#3b82f6",
    },
  },
  filterChipsRow: {
    display: "flex",
    gap: 6,
    alignItems: "center",
    flexWrap: "wrap",
    marginLeft: "auto",
  },
  filterChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "3px 8px",
    fontSize: 11,
    fontWeight: 500,
    background: "#f3f4f6",
    color: "#374151",
    borderRadius: 12,
    cursor: "pointer",
    "&:hover": { background: "#e5e7eb" },
  },
  filterChipClose: {
    fontSize: 12,
    color: "#6b7280",
  },
  clearAllLink: {
    fontSize: 11,
    fontWeight: 500,
    color: "#6b7280",
    cursor: "pointer",
    padding: "3px 6px",
    "&:hover": { color: "#1f2937", textDecoration: "underline" },
  },
  routeTableCard: {
    background: "#fff",
    borderRadius: 8,
    padding: 0,
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
    border: "1px solid #f3f4f6",
    flex: 1,
    overflow: "auto",
  },
  routeTable: {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
    fontSize: 13,
  },
  routeGroupRow: {
    background: "#f9fafb",
  },
  routeGroupTh: {
    padding: "6px 12px",
    fontSize: 10,
    fontWeight: 600,
    color: "#6b7280",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    textAlign: "center",
    borderBottom: "1px solid #e5e7eb",
    background: "#f9fafb",
  },
  routeTh: {
    position: "sticky",
    top: 0,
    background: "#fff",
    padding: "10px 12px",
    fontSize: 11,
    fontWeight: 500,
    color: "#374151",
    textTransform: "uppercase",
    letterSpacing: 0.4,
    textAlign: "right",
    whiteSpace: "nowrap",
    borderBottom: "2px solid #e5e7eb",
    cursor: "pointer",
    userSelect: "none",
    "&:hover": { background: "#f9fafb" },
  },
  routeThFirst: {
    textAlign: "left",
  },
  routeThSortIcon: {
    fontSize: 14,
    verticalAlign: "middle",
    marginLeft: 2,
    opacity: 0.4,
  },
  routeThSortIconActive: {
    opacity: 1,
    color: ({ clientColor }) => clientColor || "#1f2937",
  },
  routeGroupSep: {
    borderLeft: "1px solid #e5e7eb",
  },
  routeRow: {
    borderBottom: "1px solid #f3f4f6",
    transition: "background 0.1s",
    "&:hover": { background: "#fafafa" },
  },
  routeCell: {
    padding: "8px 12px",
    textAlign: "right",
    fontWeight: 300,
    color: "#1f2937",
    whiteSpace: "nowrap",
  },
  routeCellFirst: {
    textAlign: "left",
    fontWeight: 400,
    color: "#111827",
  },
  routeCellMuted: {
    color: "#9ca3af",
  },
  destinoBadge: {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 10,
    fontSize: 11,
    fontWeight: 500,
    marginLeft: 4,
  },
  routeFooter: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "8px 14px",
    fontSize: 11,
    color: "#6b7280",
    background: "#fafafa",
    borderTop: "1px solid #f3f4f6",
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
  },
  // Mock banner
  mockBanner: {
    position: "absolute",
    top: 8,
    right: 16,
    padding: "3px 10px",
    background: "#fef3c7",
    color: "#92400e",
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: 0.5,
    textTransform: "uppercase",
    borderRadius: 4,
    border: "1px solid #fde68a",
    zIndex: 10,
  },
}));

// -----------------------------------------------------------------------------
// Template components
// -----------------------------------------------------------------------------
function ScorecardRankingTemplate({ board, classes, lang, clientColor }) {
  const rk = board.ranking;
  const maxValue = Math.max(...rk.items.map((i) => i.value));
  const benchmarkPct = rk.benchmark ? (rk.benchmark / maxValue) * 100 : null;

  return (
    <>
      <div className={classes.kpiRow}>
        {board.kpis.map((kpi) => {
          const TrendIcon = getTrendIcon(kpi.delta, kpi.trend);
          const trendColor = getTrendColor(kpi.delta, kpi.trend);
          return (
            <div className={classes.kpiCard} key={kpi.code}>
              <div className={classes.kpiLabel}>{kpi.label[lang] || kpi.label.en}</div>
              <div className={classes.kpiValue}>
                {formatValue(kpi.value, kpi.format)}
              </div>
              <div className={classes.kpiDelta} style={{ color: trendColor }}>
                <TrendIcon style={{ fontSize: 16 }} />
                <span>{formatDelta(kpi.delta)}</span>
                <span style={{ color: "#9ca3af", fontWeight: 400 }}>
                  &nbsp;vs mes ant.
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <div className={classes.rankingCard}>
        <div className={classes.rankingHeader}>
          <div className={classes.rankingTitle}>
            {rk.metricLabel[lang] || rk.metricLabel.en} —{" "}
            {rk.dimensionLabel[lang] || rk.dimensionLabel.en}
          </div>
          {rk.benchmark != null && (
            <div className={classes.rankingBenchmark}>
              Promedio: <b>{formatValue(rk.benchmark, rk.format)}</b>
            </div>
          )}
        </div>
        <div className={classes.rankingList}>
          {rk.items.map((item) => {
            const pct = (item.value / maxValue) * 100;
            const aboveBenchmark =
              rk.trend === "lower_is_better"
                ? item.value > rk.benchmark
                : item.value < rk.benchmark;
            return (
              <div className={classes.rankingRow} key={item.name}>
                <div className={classes.rankingName}>{item.name}</div>
                <div className={classes.barContainer}>
                  <div
                    className={classes.bar}
                    style={{
                      width: `${pct}%`,
                      background: aboveBenchmark
                        ? "#ef4444"
                        : clientColor || "#3b82f6",
                      opacity: 0.85,
                    }}
                  />
                  {benchmarkPct != null && (
                    <div
                      className={classes.benchmarkLine}
                      style={{ left: `${benchmarkPct}%` }}
                    />
                  )}
                </div>
                <div className={classes.rankingValue}>
                  {formatValue(item.value, rk.format)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

function HeatmapMatrixTemplate({ board, classes, lang }) {
  // Compute per-column values for scaling
  const columnValues = board.metrics.map((_, colIdx) =>
    board.rows.map((row) => row.values[colIdx])
  );

  return (
    <div className={classes.heatmapCard}>
      <table className={classes.heatTable}>
        <thead>
          <tr className={classes.heatThRow}>
            <th className={`${classes.heatTh} ${classes.heatThFirst}`}>
              {board.rows_dimension.label[lang] || board.rows_dimension.label.en}
            </th>
            {board.metrics.map((m) => (
              <th className={classes.heatTh} key={m.code}>
                {m.label[lang] || m.label.en}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {board.rows.map((row) => (
            <tr className={classes.heatRow} key={row.name}>
              <td className={`${classes.heatCell} ${classes.heatCellFirst}`}>
                {row.name}
              </td>
              {row.values.map((v, ci) => {
                const metric = board.metrics[ci];
                const bg = getHeatColor(
                  v,
                  columnValues[ci],
                  metric.color_scale
                );
                return (
                  <td
                    key={ci}
                    className={classes.heatCell}
                    style={{ background: bg }}
                  >
                    {formatValue(v, metric.format)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DecompositionTemplate({ board, classes, lang }) {
  const total = board.groups.reduce((s, g) => s + g.value, 0);
  return (
    <div className={classes.decompCard}>
      <div className={classes.decompTotal}>
        <div className={classes.decompTotalLabel}>
          {board.total_label[lang] || board.total_label.en}
        </div>
        <div className={classes.decompTotalValue}>
          {formatValue(total, board.format)}
        </div>
      </div>
      <div className={classes.decompStacked}>
        {board.groups.map((g, i) => {
          const pct = (g.value / total) * 100;
          return (
            <Tooltip
              key={i}
              title={`${g.name[lang] || g.name.en}: ${formatValue(
                g.value,
                board.format
              )} (${pct.toFixed(1)}%)`}
            >
              <div
                className={classes.decompSegment}
                style={{
                  width: `${pct}%`,
                  background: g.color,
                }}
              >
                {pct > 8 ? `${pct.toFixed(0)}%` : ""}
              </div>
            </Tooltip>
          );
        })}
      </div>
      <div className={classes.decompLegend}>
        {board.groups.map((g, i) => {
          const pct = (g.value / total) * 100;
          return (
            <div className={classes.decompLegendItem} key={i}>
              <div
                className={classes.decompLegendSwatch}
                style={{ background: g.color }}
              />
              <div className={classes.decompLegendName}>
                {g.name[lang] || g.name.en}
              </div>
              <div className={classes.decompLegendValue}>
                {pct.toFixed(1)}%
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function BenchmarkTargetTemplate({ board, classes, clientColor }) {
  return (
    <div className={classes.benchmarkCard}>
      {board.items.map((item) => {
        const pctActual = Math.min(100, item.actual * 100);
        const pctTarget = Math.min(100, item.target * 100);
        const met = item.actual >= item.target;
        return (
          <div className={classes.bulletRow} key={item.name}>
            <div className={classes.bulletName}>{item.name}</div>
            <div className={classes.bulletTrack}>
              <div
                className={classes.bulletFill}
                style={{
                  width: `${pctActual}%`,
                  background: met ? "#10b981" : "#f59e0b",
                }}
              />
              <div
                className={classes.bulletTarget}
                style={{ left: `${pctTarget}%` }}
              />
            </div>
            <div className={classes.bulletValue}>
              {formatValue(item.actual, board.format)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// -----------------------------------------------------------------------------
// RouteTableTemplate — REAL CASE for CCU Argentina logistics flows
// -----------------------------------------------------------------------------
function RouteTableTemplate({ board, classes, lang, clientColor }) {
  // Filter state (uncontrolled selects — the mock uses in-memory filtering)
  const [filters, setFilters] = useState({
    sku: "",
    cliente: "",
    cd: "",
    origen: "",
    destino: "",
    periodo: "2025-02",
  });
  const [sort, setSort] = useState({
    column: board.defaultSort?.column || "cost_per_pallet",
    order: board.defaultSort?.order || "desc",
  });

  // Build unique options from the mock rows
  const opts = useMemo(() => {
    const u = (fn) => Array.from(new Set(board.rows.map(fn))).filter(Boolean).sort();
    const deposits = u((r) =>
      r.origen_type === "deposit" ? r.origen : null
    );
    const destinos = u((r) => r.destino);
    return {
      sku: ["11083", "11085", "22014", "22015", "35102"], // mock list
      cliente: ["CARREFOUR", "COTO", "JUMBO", "LA ANÓNIMA", "Otros"],
      cd: deposits,
      origen: u((r) => r.origen),
      destino: destinos,
      periodo: ["2025-02", "2025-01", "2024-12"],
    };
  }, [board.rows]);

  // Apply filters (mock — only origen/destino/cd affect the visible rows here,
  // since the other filters would alter the aggregation in the real API).
  const filtered = useMemo(() => {
    return board.rows.filter((r) => {
      if (filters.cd && r.origen_type === "deposit" && r.origen !== filters.cd)
        return false;
      if (filters.origen && r.origen !== filters.origen) return false;
      if (filters.destino && r.destino !== filters.destino) return false;
      return true;
    });
  }, [board.rows, filters]);

  // Sort
  const sorted = useMemo(() => {
    const col = sort.column;
    const multiplier = sort.order === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const av = a[col];
      const bv = b[col];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "string")
        return av.localeCompare(bv) * multiplier;
      return (av - bv) * multiplier;
    });
  }, [filtered, sort]);

  // Precompute column values for heat coloring
  const heatValuesByCol = useMemo(() => {
    const result = {};
    board.columns.forEach((c) => {
      if (c.color_scale && c.color_scale !== "neutral") {
        result[c.code] = sorted
          .map((r) => r[c.code])
          .filter((v) => v != null && !Number.isNaN(v));
      }
    });
    return result;
  }, [sorted, board.columns]);

  const toggleSort = (col) => {
    setSort((prev) =>
      prev.column === col
        ? { column: col, order: prev.order === "asc" ? "desc" : "asc" }
        : { column: col, order: "desc" }
    );
  };

  const activeFilters = Object.entries(filters).filter(
    ([k, v]) => v && k !== "periodo"
  );

  const clearFilter = (key) =>
    setFilters((prev) => ({ ...prev, [key]: "" }));

  const clearAll = () =>
    setFilters({
      sku: "",
      cliente: "",
      cd: "",
      origen: "",
      destino: "",
      periodo: filters.periodo,
    });

  const renderSortIcon = (col) => {
    const active = sort.column === col;
    if (!active)
      return (
        <UnfoldMore
          className={classes.routeThSortIcon}
        />
      );
    const Icon = sort.order === "asc" ? ArrowUpward : ArrowDownward;
    return (
      <Icon
        className={cx(classes.routeThSortIcon, classes.routeThSortIconActive)}
      />
    );
  };

  const destinoBadgeStyle = (type) => {
    switch (type) {
      case "client_t1":
        return { background: "#dbeafe", color: "#1e40af" };
      case "client_porteo":
        return { background: "#ede9fe", color: "#5b21b6" };
      case "deposit":
      default:
        return { background: "#f3f4f6", color: "#4b5563" };
    }
  };

  const destinoBadgeLabel = (type) => {
    switch (type) {
      case "client_t1": return "T1";
      case "client_porteo": return "Porteo";
      case "deposit": return "Depósito";
      default: return "";
    }
  };

  return (
    <div className={classes.routeContainer}>
      {/* Filter bar */}
      <div className={classes.filterBar}>
        <FilterList className={classes.filterBarIcon} />
        {board.filters.map((f) => (
          <div className={classes.filterField} key={f.code}>
            <div className={classes.filterLabel}>
              {f.label[lang] || f.label.en}
            </div>
            <select
              className={classes.filterSelect}
              value={filters[f.code] || ""}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, [f.code]: e.target.value }))
              }
            >
              <option value="">
                {f.code === "periodo" ? "" : "Todos"}
              </option>
              {(opts[f.code] || []).map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </div>
        ))}
        <div className={classes.filterChipsRow}>
          {activeFilters.length > 0 && (
            <span
              className={classes.clearAllLink}
              onClick={clearAll}
            >
              Limpiar filtros
            </span>
          )}
        </div>
      </div>

      {/* Table */}
      <div className={classes.routeTableCard}>
        <table className={classes.routeTable}>
          <thead>
            {/* Group headers */}
            <tr className={classes.routeGroupRow}>
              {board.columnGroups.map((g, gi) => (
                <th
                  key={gi}
                  className={classes.routeGroupTh}
                  colSpan={g.cols.length}
                  style={gi > 0 ? { borderLeft: "1px solid #e5e7eb" } : {}}
                >
                  {g.label[lang] || g.label.en}
                </th>
              ))}
            </tr>
            {/* Column headers */}
            <tr>
              {board.columns.map((col, ci) => {
                const isFirstInGroup =
                  ci > 0 &&
                  board.columnGroups.some((g) => g.cols[0] === col.code);
                return (
                  <th
                    key={col.code}
                    className={cx(classes.routeTh, {
                      [classes.routeThFirst]: col.type === "text",
                      [classes.routeGroupSep]: isFirstInGroup,
                    })}
                    onClick={() => toggleSort(col.code)}
                  >
                    {col.label[lang] || col.label.en}
                    {renderSortIcon(col.code)}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {sorted.map((row, ri) => (
              <tr className={classes.routeRow} key={ri}>
                {board.columns.map((col, ci) => {
                  const isFirstInGroup =
                    ci > 0 &&
                    board.columnGroups.some((g) => g.cols[0] === col.code);
                  const value = row[col.code];
                  const isText = col.type === "text";
                  const isMuted = value == null;
                  const bg =
                    col.color_scale && col.color_scale !== "neutral" && value != null
                      ? getHeatColor(
                          value,
                          heatValuesByCol[col.code] || [],
                          col.color_scale
                        )
                      : "transparent";

                  return (
                    <td
                      key={col.code}
                      className={cx(classes.routeCell, {
                        [classes.routeCellFirst]: isText,
                        [classes.routeCellMuted]: isMuted,
                        [classes.routeGroupSep]: isFirstInGroup,
                      })}
                      style={{ background: bg }}
                    >
                      {isText ? (
                        col.code === "destino" ? (
                          <>
                            {value}
                            {row.destino_type && row.destino_type !== "deposit" ? (
                              <span
                                className={classes.destinoBadge}
                                style={destinoBadgeStyle(row.destino_type)}
                              >
                                {destinoBadgeLabel(row.destino_type)}
                              </span>
                            ) : null}
                          </>
                        ) : (
                          value
                        )
                      ) : isMuted ? (
                        "–"
                      ) : (
                        formatValue(value, col.format)
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        <div className={classes.routeFooter}>
          <span>
            {sorted.length} ruta{sorted.length === 1 ? "" : "s"}
          </span>
          <span style={{ fontStyle: "italic" }}>
            Colores: rutas más caras por unidad destacadas en rojo
          </span>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Main component
// -----------------------------------------------------------------------------
export default function Indicators() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "en").split("-")[0];
  const { clientColor } = useThemeColors();
  const classes = useStyles({ clientColor });

  const [categoryCode, setCategoryCode] = useState(MOCK_CATEGORIES[0].code);
  const activeCategory = useMemo(
    () => MOCK_CATEGORIES.find((c) => c.code === categoryCode),
    [categoryCode]
  );

  const [boardCodeByCategory, setBoardCodeByCategory] = useState({
    logistics: "logistics_efficiency",
    commercial: "channel_performance",
    manufacturing: "plant_efficiency",
  });
  const boardCode = boardCodeByCategory[categoryCode];
  const board = MOCK_BOARDS[boardCode];

  const renderTemplate = () => {
    if (!board) return null;
    switch (board.template) {
      case "scorecard_ranking":
        return (
          <ScorecardRankingTemplate
            board={board}
            classes={classes}
            lang={lang}
            clientColor={clientColor}
          />
        );
      case "heatmap_matrix":
        return <HeatmapMatrixTemplate board={board} classes={classes} lang={lang} />;
      case "decomposition":
        return <DecompositionTemplate board={board} classes={classes} lang={lang} />;
      case "benchmark_target":
        return (
          <BenchmarkTargetTemplate
            board={board}
            classes={classes}
            clientColor={clientColor}
          />
        );
      case "route_table":
        return (
          <RouteTableTemplate
            board={board}
            classes={classes}
            lang={lang}
            clientColor={clientColor}
          />
        );
      default:
        return <div>Template not implemented: {board.template}</div>;
    }
  };

  return (
    <div className={classes.pageWrapper}>
      {board.template !== "route_table" && (
        <div className={classes.mockBanner}>Mock — UI draft</div>
      )}
      <div className={classes.root}>
        <div className={classes.sidebar}>
          <div className={classes.sidebarTitle}>Categorías</div>
          {MOCK_CATEGORIES.map((cat) => {
            const Icon = cat.icon;
            const active = cat.code === categoryCode;
            return (
              <div
                key={cat.code}
                className={`${classes.categoryItem} ${
                  active ? classes.categoryItemActive : ""
                }`}
                onClick={() => setCategoryCode(cat.code)}
              >
                <Icon style={{ fontSize: 20 }} />
                <span>{cat.name[lang] || cat.name.en}</span>
              </div>
            );
          })}
        </div>

        <div className={classes.content}>
          <div className={classes.tabs}>
            {activeCategory.boards.map((bc) => {
              const b = MOCK_BOARDS[bc];
              if (!b) return null;
              const active = bc === boardCode;
              return (
                <div
                  key={bc}
                  className={`${classes.tab} ${active ? classes.tabActive : ""}`}
                  onClick={() =>
                    setBoardCodeByCategory((prev) => ({
                      ...prev,
                      [categoryCode]: bc,
                    }))
                  }
                >
                  {b.name[lang] || b.name.en}
                </div>
              );
            })}
          </div>

          <div className={classes.boardHeader}>
            <div>
              <div className={classes.boardTitle}>
                {board.name[lang] || board.name.en}
              </div>
              {board.description && (
                <div className={classes.boardDescription}>
                  {board.description[lang] || board.description.en}
                </div>
              )}
            </div>
            <Tooltip title="Datos mock — visualización en diseño">
              <InfoOutlined
                style={{ fontSize: 16, color: "#9ca3af", marginLeft: 8 }}
              />
            </Tooltip>
          </div>

          <div className={classes.canvas}>{renderTemplate()}</div>
        </div>
      </div>
    </div>
  );
}
